"use client";

import { useState } from "react";
import { useCoach, type CoachRequest } from "./useCoach";

/**
 * Coach box: a primary "explain" action plus follow-up questions about the same
 * position. The request is rebuilt by the parent whenever the position changes.
 */
function ActiveCoachPanel({
  request,
  label = "Explain",
  compact = false,
}: {
  request: Omit<CoachRequest, "mode" | "question" | "history"> & { mode: "move" | "position" };
  label?: string;
  compact?: boolean;
}) {
  const coach = useCoach();
  const [history, setHistory] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [question, setQuestion] = useState("");
  const key = `${request.mode}|${request.fen}|${request.moveSan ?? ""}`;
  const [lastKey, setLastKey] = useState(key);

  if (lastKey !== key) {
    setLastKey(key);
    setHistory([]);
    coach.reset();
  }

  const explain = async () => {
    const answer = await coach.ask(request);
    if (!answer) return;
    // The conversation must start with a user turn, so record the implicit request.
    setHistory([
      { role: "user", content: request.mode === "move" ? `Explain the move ${request.moveSan}.` : "Explain this position." },
      { role: "assistant", content: answer },
    ]);
  };

  const followUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q) return;
    setQuestion("");
    const prior = history;
    const answer = await coach.ask({ ...request, mode: "question", question: q, history: prior.slice(-6) });
    if (!answer) return;
    setHistory([...prior, { role: "user", content: q }, { role: "assistant", content: answer }]);
  };

  return (
    <section aria-label="AI coach" className={`rounded-xl border border-border bg-surface ${compact ? "p-3" : "p-4"}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-base">Coach</h3>
        {coach.source && (
          <span className="text-xs text-muted">
            {coach.source === "template" ? "Position notes" : "AI coach"}
          </span>
        )}
      </div>
      {!coach.text && !coach.loading && (
        <button onClick={explain} className="mt-2 w-full rounded-lg bg-accent px-3 py-2 text-sm font-medium text-accent-ink hover:opacity-90">
          {label}
        </button>
      )}
      {(coach.text || coach.loading) && (
        <div className="mt-2 whitespace-pre-wrap text-sm leading-relaxed" aria-live="polite" aria-busy={coach.loading}>
          {coach.text || <span className="text-muted">Thinking…</span>}
        </div>
      )}
      {coach.error && <p className="mt-2 text-sm text-danger">{coach.error}</p>}
      {coach.source === "template" && !coach.loading && <p className="coach-availability">These notes come from the chess engine. Follow-up questions are currently unavailable.</p>}
      {history.length > 0 && !coach.loading && coach.source !== "template" && !coach.error && (
        <form onSubmit={followUp} className="mt-3 flex gap-2">
          <label className="sr-only" htmlFor="coach-q">
            Ask the coach a follow-up question
          </label>
          <input
            id="coach-q"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            maxLength={500}
            placeholder="Ask about this position…"
            className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-3 py-1.5 text-sm"
          />
          <button disabled={!question.trim()} className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-2">Ask</button>
        </form>
      )}
    </section>
  );
}

// Temporarily hide coaching without mounting its request hooks.
const COACH_ENABLED = false;
export function CoachPanel(props: Parameters<typeof ActiveCoachPanel>[0]) {
  return COACH_ENABLED ? <ActiveCoachPanel {...props} /> : null;
}
